import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NgxPermissionsService } from 'ngx-permissions';
import { exhaustMap, finalize, map, switchMap, take, tap } from 'rxjs/operators';

import type { TokenObtainRequest } from '../../../core/api/schema';
import { ExtraUserPermissions, REPORT_DETAIL_PAGE } from '../../../core/constants';
import { injectCatchHttpError } from '../../../core/errors/catch-http-error.util';
import { DateTimeService } from '../../../core/services/date-time/date-time.service';
import { UtilsService } from '../../../core/services/helpers/utils.service';
import { injectQueryParams } from '../../../core/utils/route-params.codec';
import { RETURN_URL_QUERY_PARAMS } from '../../auth.route-params';
import { useLocale } from '../../../core/store/translations/translations.actions';
import type { User } from '../../../user/models/user';
import { EnumFeatureFlags, UserTypeEnum } from '../../../user/models/user';
import { checkProjectsExistence, setUserCounts } from '../../../user/store/user-info/user-info.actions';
import { loadLocalizationSettings } from '../../../user/store/user-settings/user-settings.actions';
import type { AuthResponse } from '../../models';
import { ApiAuthService } from '../../services/api-auth.service';
import { AuthService } from '../../services/auth.service';
import { CurrentUserService } from '../../../user/services/current-user.service';

import {
  getLoginFeatures,
  getLoginFeaturesSuccess,
  loadPermissions,
  login,
  loginFailure,
  loginSuccess,
  logout,
  renewToken,
} from './auth.actions';

// The token is its own refresh credential and expires with it, so a late refresh is a logout. Half
// its life leaves minutes of slack for a tab whose timers the browser is throttling.
const REFRESH_AT_FRACTION_OF_LIFE = 0.5;

@Injectable()
export class AuthEffects {
  private apiAuthService = inject(ApiAuthService);
  private currentUser = inject(CurrentUserService);
  private authService = inject(AuthService);
  private readonly queryParams = injectQueryParams(RETURN_URL_QUERY_PARAMS);
  private dateTimeService = inject(DateTimeService);
  private jwtHelper = inject(JwtHelperService);
  private permissionsService = inject(NgxPermissionsService);
  private store = inject<Store>(Store);

  private readonly actions$ = inject<Actions>(Actions);
  private utils: UtilsService = inject(UtilsService);
  private readonly catchHttpError = injectCatchHttpError();

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      map(action => action.loginData),
      exhaustMap((auth: TokenObtainRequest) =>
        this.apiAuthService.login(auth).pipe(
          switchMap(res => {
            const serverAuthTime = res.headers.get('date');
            const userInfo = res.body;
            if (!userInfo) {
              return [loginFailure({ error: undefined })];
            }
            this.handleAuthResponse(userInfo, serverAuthTime);
            this.scheduleTokenRefresh(userInfo);
            return [
              loginSuccess(),
              useLocale({ locale: userInfo.user.locale }),
              loadLocalizationSettings(),
              loadPermissions({ user: userInfo.user }),
              checkProjectsExistence(),
              setUserCounts(),
            ];
          }),
          this.catchHttpError(error => {
            this.utils.storage.clearReturnUrl();
            return loginFailure({ error });
          }),
        ),
      ),
    ),
  );

  renewToken$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(renewToken),
        map(action => action.token),
        tap(token => {
          this.refreshToken(token);
        }),
      ),
    { dispatch: false },
  );

  getLoginFeatures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getLoginFeatures),
      exhaustMap(() => this.apiAuthService.getLoginFeatures().pipe(map(res => getLoginFeaturesSuccess({ loginFeatures: res })))),
    ),
  );

  loadPermissions$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadPermissions),
        map(action => action.user),
        tap((user: User) => {
          const permissions = user.company.feature_flags.map(f => f.feature_flag_type_name);
          this.permissionsService.loadPermissions(permissions);
          if (permissions.includes(EnumFeatureFlags.INTERNAL_LARI_ITEMS) && user.may_create_internal_lari_items) {
            this.permissionsService.addPermission(ExtraUserPermissions.CanCreateLariItems);
          }
          if (user.user_type === UserTypeEnum.TICKETING) {
            this.permissionsService.addPermission(ExtraUserPermissions.TicketingOnly);
          }
        }),
      ),
    { dispatch: false },
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginSuccess),
        tap(() => {
          let logoutFrom;
          switch (this.utils.storage.get('returnUrl')) {
            case REPORT_DETAIL_PAGE:
              logoutFrom = '/report';
              break;
            default:
              logoutFrom = null;
          }

          const linkFromQueryParams = this.queryParams().returnUrl;
          const returnUrlFromQueryParams = linkFromQueryParams && linkFromQueryParams !== '/' ? linkFromQueryParams : undefined;
          const returnUrl = returnUrlFromQueryParams || this.utils.storage.get('returnUrl');
          void this.goToAuth(returnUrl || logoutFrom || undefined);
          this.utils.storage.clearReturnUrl();
        }),
      ),
    { dispatch: false },
  );

  logOut$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logout),
        tap(() => {
          this.cancelTokenRefresh();
          void this.goToUnAuth();
        }),
      ),
    { dispatch: false },
  );

  private refreshTimer: ReturnType<typeof setTimeout> | undefined;
  private isRefreshing = false;
  goToAuth = this.utils.navigation.goToAuthPage;
  goToUnAuth = this.utils.navigation.goToUnAuthPage;

  cancelTokenRefresh() {
    if (this.refreshTimer !== undefined) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }
  }

  scheduleTokenRefresh(res: AuthResponse) {
    const lifetimeMs = this.tokenLifetimeMs(res.token);
    const delayMs = lifetimeMs === null ? res.renew_ms : Math.min(res.renew_ms, lifetimeMs * REFRESH_AT_FRACTION_OF_LIFE);

    this.cancelTokenRefresh();
    // a non-positive period would busy-loop the refresh endpoint, and a session that asks for one
    // cannot be renewed anyway
    if (!(delayMs > 0)) {
      this.authService.logoutByHTTPInterception('/api/api-token-refresh/');
      return;
    }

    this.refreshTimer = setTimeout(() => {
      // another tab may have refreshed while this one was throttled, and its token is the live one
      const token = this.utils.storage.token;
      if (token) {
        this.refreshToken(token);
      }
    }, delayMs);
  }

  private tokenLifetimeMs(token: string): number | null {
    try {
      const decoded = this.jwtHelper.decodeToken<{ exp: number; iat: number }>(token);
      // a duration, not an instant, so a client clock that disagrees with the server's does not shift it
      return decoded ? (decoded.exp - decoded.iat) * 1000 : null;
    } catch {
      return null;
    }
  }

  refreshToken(token: string) {
    // Guard against overlapping refreshes: never start a new one while one is still in flight.
    if (this.isRefreshing) {
      return;
    }
    this.isRefreshing = true;
    this.apiAuthService
      .refreshToken(token)
      .pipe(
        take(1),
        tap(res => {
          const serverAuthTime = res.headers.get('date');
          const userInfo = res.body;
          if (!userInfo) {
            return;
          }
          this.handleAuthResponse(userInfo, serverAuthTime);
          this.scheduleTokenRefresh(userInfo);
        }),
        finalize(() => {
          this.isRefreshing = false;
        }),
      )
      .subscribe();
  }

  handleAuthResponse(userInfo: AuthResponse, authTime: string | null) {
    const { user, token } = userInfo;
    if (authTime) {
      this.utils.storage.set('authTime', authTime);
    }
    this.currentUser.set(user);
    this.dateTimeService.userLocale = user.locale;
    this.dateTimeService.updateFormats(user);
    this.store.dispatch(loadPermissions({ user }));
    if (token) {
      this.utils.storage.token = token;
    }
  }
}
